/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {
    Box,
    Flex,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import PropTypes from 'prop-types'
import React from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import Link from '../../../components/link/index'
import {colors} from '../../../constants'
import CurrencyFormatter from '../../currency-formatter/CurrencyFormatter'
import CartDrawerCta from './cart-drawer-cta'

const CartDrawerOrderSummary = ({
    basket,
    isEstimate = false,
    fontSize = '16px',
    onClose
}) => {
    const navigate = useNavigation()
    const {formatMessage} = useIntl()
    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }
    const shippingItem = basket.shippingItems?.[0]

    return (
        <Stack data-testid="sf-order-summary" spacing={4} height="232px">
            <Flex flexDirection="column" gap="16px">
                <Stack w="full">
                    <Box
                        borderY="1px solid"
                        borderColor={colors.lightGray}
                        paddingY="24px"
                        fontWeight="300"
                        fontSize="16px"
                        fontFamily="Roboto"
                        color={colors.lightGray}
                    >
                        <Flex justify="space-between">
                            <Flex alignItems="center">
                                <Text
                                    lineHeight={1}
                                    fontSize="16px"
                                    fontFamily="Roboto"
                                    fontWeight="300"
                                >
                                    <FormattedMessage
                                        defaultMessage="Tax"
                                        id="order_summary.label.tax"
                                    />
                                </Text>
                            </Flex>

                            <Text
                                fontSize={fontSize}
                                fontFamily="Roboto"
                                fontWeight="400"
                            >
                                <CurrencyFormatter
                                    value={basket.taxTotal}
                                    currency={basket.currency}
                                />
                            </Text>
                        </Flex>
                        {basket?.orderPriceAdjustments && (
                            <Flex justify="space-between">
                                <Flex alignItems="center">
                                    <Text
                                        lineHeight={1}
                                        fontSize="16px"
                                        fontFamily="Roboto"
                                        fontWeight="300"
                                    >
                                        <FormattedMessage
                                            defaultMessage="Discount"
                                            id="order_summary.label.discount"
                                        />
                                    </Text>
                                </Flex>

                                <Text
                                    fontSize={fontSize}
                                    fontFamily="Roboto"
                                    fontWeight="400"
                                    color={colors.black}
                                >
                                    <CurrencyFormatter
                                        value={
                                            basket?.orderPriceAdjustments[0]
                                                ?.price
                                        }
                                        currency={basket.currency}
                                    />
                                </Text>
                            </Flex>
                        )}
                        <Flex justify="space-between">
                            <Flex alignItems="center">
                                <Text
                                    lineHeight={1}
                                    fontSize={fontSize}
                                    fontFamily="Roboto"
                                    fontWeight="300"
                                >
                                    <FormattedMessage
                                        defaultMessage="Shipping"
                                        id="order_summary.label.shipping"
                                    />
                                </Text>
                            </Flex>

                            {shippingItem?.priceAdjustments?.some(
                                ({appliedDiscount}) =>
                                    appliedDiscount?.type === 'free'
                            ) ? (
                                <Text
                                    as="span"
                                    color="green.700"
                                    textTransform="uppercase"
                                    fontSize={fontSize}
                                >
                                    <FormattedMessage
                                        defaultMessage="Free"
                                        id="order_summary.label.free"
                                    />
                                </Text>
                            ) : basket?.shippingTotal ? (
                                <Text
                                    fontSize={fontSize}
                                    fontFamily="Roboto"
                                    fontWeight="400"
                                >
                                    <CurrencyFormatter
                                        value={basket.shippingTotal}
                                        currency={basket.currency}
                                    />
                                </Text>
                            ) : (
                                <Text
                                    fontSize={fontSize}
                                    fontFamily="Roboto"
                                    fontWeight="400"
                                    color={colors.black}
                                >
                                    <FormattedMessage
                                        defaultMessage="Calculated at checkout"
                                        id="shipping_amount.text.calculated_at_checkout"
                                    />
                                </Text>
                            )}
                        </Flex>
                    </Box>
                </Stack>
                <Stack spacing={4} w="full">
                    <Flex
                        w="full"
                        justify="space-between"
                        fontWeight="300"
                        fontSize="16px"
                        fontFamily="Roboto"
                    >
                        {isEstimate ? (
                            <Text color={colors.lightGray}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total"
                                />
                            </Text>
                        ) : (
                            <Text color={colors.lightGray}>
                                <FormattedMessage
                                    defaultMessage="Order Total"
                                    id="order_summary.label.order_total"
                                />
                            </Text>
                        )}
                        <Text color={colors.black} fontWeight="600">
                            <CurrencyFormatter
                                currency={basket?.currency}
                                value={
                                    basket?.orderTotal || basket?.productTotal
                                }
                            />
                        </Text>
                    </Flex>
                </Stack>
            </Flex>
            <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
            >
                <Link
                    text={formatMessage({
                        defaultMessage: 'View Cart',
                        id: 'cart.view_cart'
                    })}
                    buttonStyles={{
                        height: '36px',
                        color: colors.black,
                        background: colors.white,
                        backgroundColor: colors.white,
                        fontSize: '16px',
                        fontFamily: 'Roboto',
                        fontWeight: '300',
                        tabIndex: '-1',
                        _focus: 'none',
                        _active: 'none',
                        textDecorationLine: 'underline'
                    }}
                    _focus="none"
                    _hover="none"
                    onLinkClick={() => {
                        onClose()
                        navigate('/cart')
                    }}
                />
                <CartDrawerCta onDrawerClose={onClose} />
            </Flex>
        </Stack>
    )
}

CartDrawerOrderSummary.propTypes = {
    basket: PropTypes.object,
    showPromoCodeForm: PropTypes.bool,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    fontSize: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
    onClose: PropTypes.func
}

export default CartDrawerOrderSummary
